import { getJwtExpiresInSeconds } from "@/lib/auth/jwt-utils";
import type { AppTokenPair } from "@/types/auth-api-types";

const ACCESS_TOKEN_COOKIE = "access_token";
const ACCESS_TOKEN_STORAGE_KEY = "access_token";
const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";

export const AUTH_SESSION_CHANGED_EVENT = "auth-session-changed";

const DEFAULT_COOKIE_MAX_AGE_SEC = 86_400;

function notifyAuthSessionChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}

function cookieMaxAgeSeconds(accessToken: string, expiresIn?: number): number {
  if (typeof expiresIn === "number" && expiresIn > 0) {
    return expiresIn;
  }
  const fromJwt = getJwtExpiresInSeconds(accessToken);
  if (fromJwt && fromJwt > 0) {
    return fromJwt;
  }
  return DEFAULT_COOKIE_MAX_AGE_SEC;
}

function writeAccessTokenCookie(token: string, maxAgeSec: number): void {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${ACCESS_TOKEN_COOKIE}=${encodeURIComponent(token)}; path=/; max-age=${maxAgeSec}; SameSite=Lax${secure}`;
}

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;

  const cookieMatch = document.cookie.match(
    new RegExp(`(?:^|; )${ACCESS_TOKEN_COOKIE}=([^;]*)`),
  );
  if (cookieMatch?.[1]) return decodeURIComponent(cookieMatch[1]);

  return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
}

export function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

/** Store the application access JWT (and optional refresh token). */
export function setAppTokens(tokens: AppTokenPair): void {
  if (typeof window === "undefined") return;

  const accessToken = tokens.accessToken.trim();
  const maxAge = cookieMaxAgeSeconds(accessToken, tokens.expiresIn);

  writeAccessTokenCookie(accessToken, maxAge);
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);

  if (tokens.refreshToken?.trim()) {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refreshToken.trim());
  }

  notifyAuthSessionChanged();
}

/** @deprecated Prefer setAppTokens — kept for mock / legacy callers. */
export function setAccessToken(token: string): void {
  setAppTokens({ accessToken: token });
}

export function clearAccessToken(): void {
  clearAppTokens();
}

export function clearAppTokens(): void {
  if (typeof window === "undefined") return;

  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${ACCESS_TOKEN_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`;
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  notifyAuthSessionChanged();
}

function clearAllCookies(): void {
  if (typeof document === "undefined") return;

  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0]?.trim();
    if (!name) continue;
    const secure =
      typeof window !== "undefined" && window.location.protocol === "https:"
        ? "; Secure"
        : "";
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}`;
  }
}

/** Wipe app session from localStorage, sessionStorage, and cookies (e.g. on logout). */
export function clearClientAuthState(): void {
  if (typeof window === "undefined") return;

  localStorage.clear();
  sessionStorage.clear();
  clearAllCookies();
  notifyAuthSessionChanged();
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken()?.trim() || getRefreshToken()?.trim());
}
